import type React from "react"
import { useCallback, useEffect, useState, useRef } from "react"
import { useParams } from "react-router-dom"
import { getRealtorById } from "../../services/realtorService"
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Fade,
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import {
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  Phone,
  LocationOn,
  Home,
  Apartment,
  Sell,
  CheckCircle,
  BarChart,
  WorkOutline,
  Verified,
  Loyalty,
  Language,
} from "@mui/icons-material"
import type { PropertyListing, BrokerData } from "../../types/Property"
import {
  TextField,
  InputAdornment,
  Pagination,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
  Snackbar,
  Alert,
  Fab,
  Zoom,
} from "@mui/material"
import { Search, FilterList, WhatsApp, Email, ContentCopy, Message } from "@mui/icons-material"
import PropertyCard from "../../components/properties/PropertyCard"
import PriceConversionModal from "../../components/modals/PriceConversionModal"
import CustomAppBar from '../../components/shared/CustomAppBar';

// Componente principal
const BrokerProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [brokerData, setBrokerData] = useState<BrokerData | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const { id } = useParams()
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [propertiesPerPage] = useState(6)
  const [openContactDialog, setOpenContactDialog] = useState(false)
  const [contactMethod, setContactMethod] = useState("email")
  const [contactMessage, setContactMessage] = useState("")
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success")
  const [transactionType, setTransactionType] = useState<string>("Todos")
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000000000])
  const [openFilterDialog, setOpenFilterDialog] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedPrice, setSelectedPrice] = useState<number>(0)

  const propertiesContainerRef = useRef<HTMLDivElement>(null)

  // Propiedades filtradas por tipo de transacción
  const salesProperties = brokerData?.propertiesListing?.filter((prop) => prop.modeDescription === "Venta") || []
  const rentalProperties = brokerData?.propertiesListing?.filter((prop) => prop.modeDescription === "Alquiler") || []
  const availableProperties = brokerData?.propertiesListing?.filter((prop) => prop.moduleStateId === 1) || []
  const soldProperties = brokerData?.propertiesListing?.filter((prop) => prop.moduleStateId !== 1) || []

  
  const fetchData = useCallback(async () => {
    try {
      const fetchedData = await getRealtorById(id ?? "0")

      const formattedProperties = fetchedData.properties.map((prop: any) => {
        const formatted = {
          ...prop,
          photos: prop.listPhotos ? prop.listPhotos.map((photo: any) => photo.url) : [],
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
          hunterFullName:  prop.hunterFullName || null,
          hunterProfileImage:  prop.hunterProfileImage || null,
        };

        return formatted;
      });

      setBrokerData({
        ...fetchedData,
        properties: formattedProperties,
        propertiesListing: formattedProperties,
      })
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setCurrentPage(1)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleOpenContactDialog = () => {
    setOpenContactDialog(true)
  }

  const handleCloseContactDialog = () => {
    setOpenContactDialog(false)
  }

  const handleContactMethodChange = (event: SelectChangeEvent) => {
    setContactMethod(event.target.value)
  }

  const handleContactMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactMessage(event.target.value)
  }

  const handleSendContact = () => {
    // Aquí iría la lógica para enviar el mensaje
    setSnackbarMessage("Mensaje enviado con éxito")
    setSnackbarSeverity("success")
    setSnackbarOpen(true)
    setOpenContactDialog(false)
    setContactMessage("")
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setSnackbarMessage("Copiado al portapapeles")
    setSnackbarSeverity("success")
    setSnackbarOpen(true)
  }

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true)
  }

  const handleCloseFilterDialog = () => {
    setOpenFilterDialog(false)
  }

  const handlePriceRangeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const newValue = Number.parseInt(event.target.value)
    const newPriceRange = [...priceRange] as [number, number]
    newPriceRange[index] = newValue
    setPriceRange(newPriceRange)
  }

  const handleApplyFilters = () => {
    setOpenFilterDialog(false)
    setCurrentPage(1) // Resetear a la primera página cuando se aplican filtros
  }

  const handleOpenWhatsApp = (phone: string) => {
    const formattedPhone = phone.replace(/\D/g, "")
    const url = `https://wa.me/${formattedPhone}?text=${encodeURIComponent("Hola, estoy interesado en tus propiedades en Alien Realty.")}`
    window.open(url, "_blank")
  }

  const handleSendEmail = (email: string) => {
    const url = `mailto:${email}?subject=${encodeURIComponent("Consulta sobre propiedades en Alien Realty")}&body=${encodeURIComponent("Hola, estoy interesado en tus propiedades en Alien Realty.")}`
    window.location.href = url
  }

  const handleConvertPrice = (price: number) => {
    setSelectedPrice(price)
    setModalOpen(true)
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress sx={{ color: "#10b981" }} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Cargando perfil del broker...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Box sx={{ p: 3, bgcolor: "#FFEBEE", maxWidth: 500, borderRadius: 2 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error:
          </Typography>
          <Typography variant="body1" color="error.dark">
            {error}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!brokerData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Box sx={{ p: 3, bgcolor: "#FFF8E1", maxWidth: 500, borderRadius: 2 }}>
          <Typography variant="body1" color="warning.dark">
            No se encontró el broker.
          </Typography>
        </Box>
      </Box>
    )
  }

  const filterProperties = (properties: PropertyListing[]) => {
    if (!Array.isArray(properties)) {
      return []
    }

    let filtered = [...properties]

    if (searchTerm) {
      const lowerCasedTerm = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (property) =>
          (property.title?.toLowerCase() || "").includes(lowerCasedTerm) ||
          (property.categoryName?.toLowerCase() || "").includes(lowerCasedTerm) ||
          (property.address_Description?.toLowerCase() || "").includes(lowerCasedTerm),
      )
    }

    if (transactionType !== "Todos") {
      filtered = filtered.filter((property) => property.modeDescription === transactionType)
    }

    filtered = filtered.filter((property) => property.price >= priceRange[0] && property.price <= priceRange[1])

    switch (tabValue) {
      case 0: // Todas
        return filtered
      case 1: // Venta
        return filtered.filter((property) => property.modeDescription === "Venta")
      case 2: // Alquiler
        return filtered.filter((property) => property.modeDescription === "Alquiler")
      case 3: // Disponibles
        return filtered.filter((property) => property.moduleStateId === 1)
      case 4: // Vendidas/Alquiladas
        return filtered.filter((property) => property.moduleStateId !== 1)
      default:
        return filtered
    }
  }

  // Obtener propiedades filtradas y paginadas
  const filteredProperties = filterProperties(brokerData.propertiesListing || [])
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage)
  const indexOfLastProperty = currentPage * propertiesPerPage
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage
  const paginatedProperties = filteredProperties.slice(indexOfFirstProperty, indexOfFirstProperty + propertiesPerPage)
  const totalProperties = filteredProperties.length

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value)

    // Scroll al contenedor de propiedades con un pequeño offset
    setTimeout(() => {
      if (propertiesContainerRef.current) {
        const yOffset = window.innerWidth <= 768 ? -100 : -150
        const y = propertiesContainerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: "smooth" })
      }
    }, 100)
  }

  return (
  <>
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <CustomAppBar/>
      {/* Header - PROFILE */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WorkOutline sx={{ color: "#10b981", fontSize: 18 }} />
          <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: "medium" }}>
            PERFIL DE BROKER
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Message />}
          onClick={handleOpenContactDialog}
          sx={{
            bgcolor: "#10b981",
            borderRadius: "20px",
            px: 3,
            transition: "all 0.3s ease",
            boxShadow: "0 4px 10px rgba(16, 185, 129, 0.2)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 15px rgba(16, 185, 129, 0.3)",
              bgcolor: "#059669",
            },
          }}
        >
          CONTACTAR
        </Button> */}
      </Box>

      {/* Perfil principal y estadísticas */}
      <Box
        sx={{
          background: "linear-gradient(145deg, #f8fffe 0%, #ecfdf5 100%)",
          borderRadius: "16px",
          p: 3,
          mb: 4,
          boxShadow: "0 10px 25px rgba(16, 185, 129, 0.08)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Grid container spacing={3}>
          {/* Foto y nombre */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Fade in={true} timeout={800}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: { xs: "100%", md: "200px" },
                    height: { xs: "200px", md: "200px" },
                    borderRadius: "8px",
                    overflow: "hidden",
                    mb: 2,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    border: "4px solid white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                    },
                  }}
                >
                  <img
                    src={brokerData.profileImage || "/assets/images/profilePicture.png"}
                    alt={brokerData.contacts.fullName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    #{brokerData.code}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {brokerData.contacts.fullName}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <LocationOn sx={{ color: "#10b981", fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Realtor • {brokerData.contacts.address}
                  </Typography>
                </Box>

                {/* Acciones rápidas */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Tooltip title="Enviar WhatsApp">
                    <IconButton
                      onClick={() => handleOpenWhatsApp(brokerData.contacts.phone)}
                      sx={{
                        bgcolor: "#25D366",
                        color: "white",
                        "&:hover": { bgcolor: "#128C7E" },
                      }}
                    >
                      <WhatsApp />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Enviar Email">
                    <IconButton
                      onClick={() => handleSendEmail(brokerData.contacts.email)}
                      sx={{
                        bgcolor: "#4285F4",
                        color: "white",
                        "&:hover": { bgcolor: "#0F9D58" },
                      }}
                    >
                      <Email />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copiar teléfono">
                    <IconButton
                      onClick={() => handleCopyToClipboard(brokerData.contacts.phone)}
                      sx={{
                        bgcolor: "#10b981",
                        color: "white",
                        "&:hover": { bgcolor: "#059669" },
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Estadísticas */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 4,
                height: "100%",
              }}
            >
              <Zoom in={true} style={{ transitionDelay: "200ms" }}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "rgba(16, 185, 129, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                    <Home sx={{ color: "#10b981", mr: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {brokerData.properties.length}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    PROPIEDADES
                  </Typography>
                </Box>
              </Zoom>

              <Zoom in={true} style={{ transitionDelay: "300ms" }}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "rgba(16, 185, 129, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                    <Sell sx={{ color: "#10b981", mr: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
                      {brokerData.salesCount}
                      <Typography component="span" sx={{ color: "error.main", fontSize: "1rem", ml: 0.5 }}>
                        ↘
                      </Typography>
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    VENTAS
                  </Typography>
                </Box>
              </Zoom>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Detalles de contacto y estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Detalles de contacto */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 3,
              borderRadius: "12px",
              boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
              height: "100%",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Phone sx={{ color: "#10b981" }} />
              <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: "medium" }}>
                DETALLES DE CONTACTO
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    bgcolor: "rgba(16, 185, 129, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Email sx={{ fontSize: 16, color: "#10b981" }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      CORREO ELECTRÓNICO
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#10b981", fontWeight: "medium" }}>
                    {brokerData.contacts.email}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    bgcolor: "rgba(16, 185, 129, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Phone sx={{ fontSize: 16, color: "#10b981" }} />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      TELÉFONO
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {brokerData.contacts.phone}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Language sx={{ color: "#10b981", fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                REDES SOCIALES
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              {brokerData.facebookNick && (
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    borderRadius: "50%",
                    p: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 5px 15px rgba(16, 185, 129, 0.2)",
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                    },
                  }}
                  href={`https://facebook.com/${brokerData.facebookNick}`}
                  target="_blank"
                >
                  <Facebook sx={{ fontSize: 16, color: "#10b981" }} />
                </IconButton>
              )}

              {brokerData.instagramNick && (
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    borderRadius: "50%",
                    p: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 5px 15px rgba(16, 185, 129, 0.2)",
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                    },
                  }}
                  href={`https://instagram.com/${brokerData.instagramNick}`}
                  target="_blank"
                >
                  <Instagram sx={{ fontSize: 16, color: "#10b981" }} />
                </IconButton>
              )}

              {brokerData.linkedinNick && (
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    borderRadius: "50%",
                    p: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 5px 15px rgba(16, 185, 129, 0.2)",
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                    },
                  }}
                  href={`https://linkedin.com/in/${brokerData.linkedinNick}`}
                  target="_blank"
                >
                  <LinkedIn sx={{ fontSize: 16, color: "#10b981" }} />
                </IconButton>
              )}

              {brokerData.xNick && (
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    borderRadius: "50%",
                    p: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 5px 15px rgba(16, 185, 129, 0.2)",
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                    },
                  }}
                  href={`https://twitter.com/${brokerData.xNick}`}
                  target="_blank"
                >
                  <Twitter sx={{ fontSize: 16, color: "#10b981" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Estadísticas del agente */}
        <Grid size={{ xs: 12, md: 6 }} mt={{ xs: 4, md: 0 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ height: "97%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <BarChart sx={{ color: "#10b981" }} />
                <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: "medium" }}>
                  ESTADÍSTICAS DEL AGENTE
                </Typography>
              </Box>

              <Box
                sx={{
                  bgcolor: "#10b981",
                  borderRadius: "12px",
                  p: 3,
                  color: "white",
                  height: "calc(100% - 30px)",
                  boxShadow: "0 10px 30px rgba(16, 185, 129, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 15px 35px rgba(16, 185, 129, 0.4)",
                  },
                }}
              >
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        TOTAL PROPIEDADES
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {brokerData.properties.length}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        VENTAS
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {brokerData.salesCount}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.15)",
                        borderRadius: "8px",
                        p: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.25)",
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Apartment sx={{ fontSize: 16 }} />
                        <Typography variant="caption">ESPECIALIDAD</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        Alquiler
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.15)",
                        borderRadius: "8px",
                        p: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.25)",
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Verified sx={{ fontSize: 16 }} />
                        <Typography variant="caption">CUALIDAD DESTACADA</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        Honestidad
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Fade>
        </Grid>
      </Grid>

      {/* Barra de búsqueda y filtros */}
      <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        <Box mt={4} display="flex" gap={1} flexWrap="nowrap">
          <TextField
            placeholder="Buscar propiedades..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              flexGrow: 1,
              minWidth: { xs: "auto", sm: "300px" },
              maxWidth: { sm: "400px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#10b981" }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={handleOpenFilterDialog}
            sx={{
              borderColor: "#10b981",
              color: "#10b981",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#059669",
                bgcolor: "rgba(16, 185, 129, 0.05)",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                display: { xs: "none", sm: "inline" },
              }}
            >
              Filtros
            </Box>
          </Button>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mr: 1 }}>
            {totalProperties} propiedades encontradas
          </Typography>
        </Box>
      </Box>

      {/* Pestañas de propiedades */}
      <Box sx={{ mb: 3 }} ref={propertiesContainerRef}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Home sx={{ color: "#10b981" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: 1, fontWeight: "medium" }}>
            PROPIEDADES
          </Typography>
        </Box>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              transition: "all 0.3s ease",
              "&:hover": {
                color: "#10b981",
                transform: "translateY(-2px)",
              },
            },
            "& .Mui-selected": { color: "#10b981" },
            "& .MuiTabs-indicator": { backgroundColor: "#10b981" },
          }}
        >
          <Tab
            icon={<Home sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={`Todas (${brokerData.properties.length})`}
          />
          <Tab icon={<Sell sx={{ fontSize: 16 }} />} iconPosition="start" label={`Venta (${salesProperties.length})`} />
          <Tab
            icon={<Apartment sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={`Alquiler (${rentalProperties.length})`}
          />
          <Tab
            icon={<CheckCircle sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={`Disponibles (${availableProperties.length})`}
          />
          <Tab
            icon={<Loyalty sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={`Vendidas/Alquiladas (${soldProperties.length})`}
          />
        </Tabs>
      </Box>

      {/* Chips de filtros activos */}
      {(searchTerm || transactionType !== "Todos" || priceRange[0] > 0 || priceRange[1] < 1000000000) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {searchTerm && (
            <Chip
              label={`Búsqueda: ${searchTerm}`}
              onDelete={() => setSearchTerm("")}
              color="primary"
              sx={{ bgcolor: "#10b981" }}
            />
          )}

          {transactionType !== "Todos" && (
            <Chip
              label={`Tipo: ${transactionType}`}
              onDelete={() => setTransactionType("Todos")}
              color="primary"
              sx={{ bgcolor: "#10b981" }}
            />
          )}

          {(priceRange[0] > 0 || priceRange[1] < 1000000000) && (
            <Chip
              label={`Precio: ₡${priceRange[0].toLocaleString()} - ₡${priceRange[1].toLocaleString()}`}
              onDelete={() => setPriceRange([0, 1000000000])}
              color="primary"
              sx={{ bgcolor: "#10b981" }}
            />
          )}
        </Box>
      )}

      {/* Grid de propiedades */}
      {paginatedProperties.length > 0 ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 3,
            }}
          >
            {paginatedProperties.map((property) => {
              return (
                <PropertyCard
                  key={property.id_Property}
                  property={property}
                  onConvertPrice={handleConvertPrice}
                  isMapView={false}
                  fromBrokerProfile={true}
                  hunterFullName={property.hunterFullName} 
                  hunterProfileImage={property.hunterProfileImage} 
                />
              )
            })}
          </Box>

          {/* Paginación */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#10b981",
                  },
                  "& .Mui-selected": {
                    bgcolor: "#10b981 !important",
                    color: "white !important",
                  },
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            bgcolor: "rgba(16, 185, 129, 0.05)",
            borderRadius: "12px",
          }}
        >
          <img
            src="/assets/images/no-results.png"
            alt="No hay resultados"
            style={{ width: "120px", marginBottom: "16px" }}
          />
          <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
            No se encontraron propiedades
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, textAlign: "center" }}>
            Intenta cambiar los filtros o términos de búsqueda
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchTerm("")
              setTransactionType("Todos")
              setPriceRange([0, 1000000000])
            }}
            sx={{
              borderColor: "#10b981",
              color: "#10b981",
              "&:hover": {
                borderColor: "#059669",
                bgcolor: "rgba(16, 185, 129, 0.05)",
              },
            }}
          >
            Limpiar filtros
          </Button>
        </Box>
      )}

      {/* Botón flotante de contacto */}
      <Zoom in={true} style={{ transitionDelay: "500ms" }}>
        <Fab
          color="primary"
          aria-label="contactar"
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            bgcolor: "#10b981",
            "&:hover": {
              bgcolor: "#059669",
            },
          }}
          onClick={handleOpenContactDialog}
        >
          <Message />
        </Fab>
      </Zoom>

      {/* Diálogo de contacto */}
      <Dialog open={openContactDialog} onClose={handleCloseContactDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#10b981", color: "white", py: 2 }}>
          Contactar a {brokerData.contacts.fullName}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Método de contacto</InputLabel>
              <Select value={contactMethod} onChange={handleContactMethodChange} label="Método de contacto">
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="phone">Llamada telefónica</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Mensaje"
              multiline
              rows={4}
              fullWidth
              value={contactMessage}
              onChange={handleContactMessageChange}
              placeholder="Escribe tu mensaje aquí..."
              sx={{ mb: 2 }}
            />

            <Box sx={{ bgcolor: "rgba(16, 185, 129, 0.05)", p: 2, borderRadius: "8px" }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                Información de contacto:
              </Typography>
              <Typography variant="body2">Email: {brokerData.contacts.email}</Typography>
              <Typography variant="body2">Teléfono: {brokerData.contacts.phone}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseContactDialog} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSendContact}
            variant="contained"
            sx={{
              bgcolor: "#10b981",
              "&:hover": {
                bgcolor: "#059669",
              },
            }}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de filtros */}
      <Dialog open={openFilterDialog} onClose={handleCloseFilterDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: "#10b981", color: "white", py: 2 }}>Filtrar propiedades</DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Tipo de transacción</InputLabel>
            <Select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              label="Tipo de transacción"
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="Venta">Venta</MenuItem>
              <MenuItem value="Alquiler">Alquiler</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Rango de precio
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              label="Mínimo"
              type="number"
              value={priceRange[0]}
              onChange={(e) => handlePriceRangeChange(e, 0)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₡</InputAdornment>,
              }}
              fullWidth
            />
            <TextField
              label="Máximo"
              type="number"
              value={priceRange[1]}
              onChange={(e) => handlePriceRangeChange(e, 1)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₡</InputAdornment>,
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setTransactionType("Todos")
              setPriceRange([0, 1000000000])
              setOpenFilterDialog(false)
            }}
            color="inherit"
          >
            Limpiar
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            sx={{
              bgcolor: "#10b981",
              "&:hover": {
                bgcolor: "#059669",
              },
            }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Modal de conversión de precios */}
      <PriceConversionModal open={isModalOpen} onClose={() => setModalOpen(false)} initialPrice={selectedPrice} />
    </Container>
      <Box
        sx={{
          height: "60px",
          display: { xs: "block", sm: "none" },
          width: "100%",
        }}
      />
   </>     
  )
}

export default BrokerProfilePage
